import { EditableCell, SearchColumn } from '@/components/common';
import { getMergedColumns } from '@/components/devicecontrol/DigiTrac/helpers';
import { ColumnsProps } from '@/Helper';
import { BaseColumns, GridDataItem } from '@/model/CommonModel';
import { ControllerContactMappedData, OptionsDigiTrac } from '@/model/DeviceAdminModel';
import { StatusObjectEnum } from '@/model/StatusViewerModel';
import { Spin, Table, TablePaginationConfig } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { ExpandableConfig, FilterValue, SorterResult, TableCurrentDataSource } from 'antd/lib/table/interface';
import React, { useState } from 'react';
import { GridDataColumnItemCustom } from '../StatusViewerTable';

type Props = StatusTableProps;

type StatusTableProps = {
	tableKey: string;
	id?: string;
	objectId?: number;
	columns: GridDataColumnItemCustom[];
	dataSource: GridDataItem[][];
	scroll?: {
		x?: string | number | true;
		y?: string | number;
	} & {
		scrollToFirstRowOnChange?: boolean;
	};
	pagination?: false | TablePaginationConfig;
	onChange?: (
		pagination: TablePaginationConfig,
		filters: Record<string, FilterValue>,
		sorter: SorterResult | SorterResult[],
		extra: TableCurrentDataSource<ControllerContactMappedData>,
		objectId?: string,
		resetTableRefreshing?: () => void
	) => void;
	className?: string;
	expandable?: ExpandableConfig<ControllerContactMappedData>;
	rowClassName?: string | any;
	onChangeSelection?: (key: BaseColumns) => void;
	handleActionScope?: (sender: OptionsDigiTrac) => Promise<void>;
	clearSearchedSelection?: () => void;
	setSearchPerformed?: (value: boolean) => void;
	handleOnSearchResults?: (searchedValue: string, tableKey: string, resetTableRefreshing: () => void) => void;
};

const maxLength = 32;

const StatusTable = (props: Props) => {
	//#region State Management Variables
	const [shouldResetSearchColumn, setShouldResetSearchColumn] = useState<boolean>(false);
	const [isTableRefreshing, setIsTableRefreshing] = useState<boolean>(false);
	//#endregion

	const clearSelection = () => {
		props.clearSearchedSelection?.();
	};

	const setSearchPerformed = (value: boolean) => {
		props.setSearchPerformed?.(value);
	};

	const resetTableRefreshing = () => {
		setIsTableRefreshing(false);
	};

	const handleOnSearchResults = (searchedValue: string) => {
		setIsTableRefreshing(true);
		props.handleOnSearchResults?.(searchedValue, props.tableKey, resetTableRefreshing);
	};

	const getSearcherColumn = () => {
		return SearchColumn({
			maxLength: 20,
			dataIndex: 'Name',
			reset: undefined,
			label: undefined,
			notVisible: undefined,
			resetSearch: shouldResetSearchColumn,
			clearSelection,
			handleResetSearch: () => setShouldResetSearchColumn(false),
			setSearchResults: (searchedValue: string) => handleOnSearchResults(searchedValue),
			setSearchedValue: undefined,
			searchColumnId: undefined,
			setSearchPerformed: (value: boolean) => setSearchPerformed(value),
			disableInternalSearchMode: true,
		});
	};

	const searcherColumn = getSearcherColumn();

	const columns = props.columns.map(c => {
		if (c.key?.toLowerCase() === 'name') {
			return { ...c, ...searcherColumn } as ColumnsProps<ControllerContactMappedData>;
		}
		return c as ColumnsProps<ControllerContactMappedData>;
	});

	const updatedColumns = getMergedColumns<ControllerContactMappedData>({
		columns,
		maxLength,
		isStatusViewer: true,
		onChangeSelection: props.onChangeSelection,
		onClickOption: props.handleActionScope,
	});

	return (
		<Spin tip={`${_('Loading')}...`} spinning={isTableRefreshing} size='large'>
			<Table
				key={props.tableKey}
				id={props.id}
				columns={updatedColumns as ColumnsType}
				components={
					props.objectId !== StatusObjectEnum.MaskedOnly && {
						body: {
							cell: EditableCell,
						},
					}
				}
				dataSource={props.dataSource}
				scroll={props.scroll}
				size={'small'}
				pagination={props.pagination}
				onChange={(
					pagination: TablePaginationConfig,
					filters: Record<string, FilterValue>,
					sorter: SorterResult | SorterResult[],
					extra: TableCurrentDataSource<ControllerContactMappedData>
				) => {
					setIsTableRefreshing(true);
					props.onChange(pagination, filters, sorter, extra, props.tableKey, resetTableRefreshing);
				}}
				className={props.className}
				expandable={props.expandable}
				rowClassName={props.rowClassName}
			/>
		</Spin>
	);
};

export { StatusTable };
